// SEQ-240: "How is this worked out" text for any P&L registry metric.
//
// The registry table (pnl_metric_registry, read by usePnlMetricRegistry.ts)
// carries no formula text, and this change may not alter the database, so the
// description is built from what the client already has: kind, section, unit,
// metric role, aggregation method, deductible, and whether the row is custom.
// The fifteen system calculated.* keys get their formula in plain words from
// CALCULATED_METRIC_FORMULAS below. A key this module does not know still gets a
// sensible generic description; describeMetric never throws.
//
// WHAT EACH FORMULA DESCRIBES, so an edit there is mirrored here:
// - computePnlBuckets, src/lib/pnl/pnlAnalytics.ts lines 1089-1177 (the
//   calculated.* block), which pnl_analytics_query reproduces in SQL
//   (supabase/migrations/20261006000100_seq193_pnl_project_month_grain.sql);
// - the wages multiplier grain rule, src/lib/pnl/wagesMultiplierGrain.ts
//   (raw wages at a single-month grain, multiplied at every other grain);
// - the fact sources, pnl_fact in
//   supabase/migrations/20260918000100_pnl_labour_hours_from_payroll.sql:
//   manual rows only (Xero is never counted), construction income and
//   retention from claims_reports, overtime and hours from KeyPay with typed
//   hours replacing payroll hours for the same project and month;
// - the materials budget, pnl_analytics_query (income times the project's
//   target materials percent, 30 percent when unset).
//
// Tested by pnlMetricDescriptions.test.ts (npm run verify:pnl-metric-descriptions).

import {
  DEFAULT_PNL_METRIC_REGISTRY,
  PNL_METRIC_KEYS,
  resolveAggregationMethod,
  resolveMetricUnit,
  type PnlAggregationMethod,
  type PnlMetricDefinition,
  type PnlUnit,
} from "../../lib/pnl/pnlAnalytics";
import { humaniseMetricKey } from "./metricOption";
import type { MetricDefinition, MetricSourceKind } from "../../components/howWorkedOut/metricDefinition";

/** A registry row as the page holds it (PnlMetricOption), or a bare definition. */
export interface DescribableMetric extends PnlMetricDefinition {
  displayName?: string;
  isCustom?: boolean;
}

/** What the surrounding widget or section knows and the registry does not. */
export interface DescribeContext {
  projects?: MetricDefinition["projects"];
  dates?: MetricDefinition["dates"];
  period?: string;
}

type CalculatedMetricKey = (typeof PNL_METRIC_KEYS.calculated)[keyof typeof PNL_METRIC_KEYS.calculated];

interface CalculatedText {
  meaning: string;
  formula: string[];
  source: { kind: MetricSourceKind; detail: string };
}

const WAGES_MULTIPLIER_LINE =
  "Over a single month, wages are counted as typed. Over a longer span, base and extra wages are multiplied by each project's wages multiplier (1.04 unless the project sets another).";

const WAGES_TOTAL_LINE =
  "Wages total is base and extra wages (with the multiplier) plus contractors, stripping, scaffold contractors and labour hire.";

const FROM_CLAIMS_AND_TYPED = "From the progress claims and the costs typed in on each project's P&L tab. Xero is not counted.";
const FROM_PAYROLL = "Payroll hours, with any hours typed in on the project P&L tab used instead for that month.";

// SEQ-240 audit B, rows 7 and 29: say exactly which projects each side covers.
const ALL_PROJECTS_DENOMINATOR_LINE =
  "Income counts every project in view, including projects with no payroll mapping, which have no overtime figure. Over mapped projects alone the percentage can be higher. When some projects are unmapped, this pop-up shows that figure under Right now.";
const HOURS_SCOPE_LINE =
  "The dollars cover every project in view, but hours only come from projects that have hours recorded (from payroll or typed in). A company-wide rate therefore reads higher than the rate over projects that have hours.";

const HOURS_BLANK_LINE = "Blank when no project in view is mapped to payroll and no hours were typed in.";

/** Plain-word formulas for the system calculated.* keys, checked against computePnlBuckets (see the header). */
export const CALCULATED_METRIC_FORMULAS: Record<CalculatedMetricKey, CalculatedText> = {
  "calculated.total_income": {
    meaning: "The value of work claimed in the period, before GST and before retention is held back.",
    formula: [
      "Adds up construction income from every progress claim in the period.",
      "Each claim counts its subtotal. An older claim with no subtotal counts its total ex GST plus its retention.",
    ],
    source: { kind: "calculated", detail: "From the progress claims (claim reports). Xero is not counted." },
  },
  "calculated.total_cost": {
    meaning: "Everything taken off income to reach profit.",
    formula: [
      "Materials budget, plus the typed costs that come off profit, plus the wages total.",
      "The materials budget is construction income times the project's materials percentage (30% when not set). The typed Materials row is not added: the budget replaces it.",
      WAGES_TOTAL_LINE,
      WAGES_MULTIPLIER_LINE,
      "Overtime pay from payroll is not added on top.",
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.net_profit": {
    meaning: "What is left of income after every cost.",
    formula: ["Total income minus total cost.", WAGES_MULTIPLIER_LINE],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.profit_percentage": {
    meaning: "How much of each dollar of income is left as profit.",
    formula: ["Net profit divided by total income, times 100.", "Blank when there is no income."],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.wages_percentage": {
    meaning: "How much of income goes on labour.",
    formula: ["Wages total divided by total income, times 100.", WAGES_TOTAL_LINE, "Blank when there is no income."],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.materials_percentage": {
    meaning: "How much of income goes on materials.",
    formula: [
      "Materials budget plus typed engineering, handrails and stairs, and misc materials, divided by total income, times 100.",
      "Blank when there is no income.",
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.overtime_pct_revenue": {
    meaning: "Overtime pay as a share of income.",
    formula: [
      "Overtime pay from payroll divided by total income, times 100.",
      ALL_PROJECTS_DENOMINATOR_LINE,
      "Blank when there is no income, or when no project in view is mapped to payroll.",
    ],
    source: { kind: "mixed", detail: "Overtime pay from payroll (KeyPay); income from the progress claims. Xero is not counted." },
  },
  "calculated.overtime_pct_wages": {
    meaning: "Overtime pay as a share of the wages total.",
    formula: [
      "Overtime pay from payroll divided by the wages total, times 100.",
      WAGES_TOTAL_LINE,
      "Blank when the wages total is zero, or when no project in view is mapped to payroll.",
    ],
    source: { kind: "mixed", detail: "Overtime pay from payroll (KeyPay); wages typed in on the project P&L tab. Xero is not counted." },
  },
  "calculated.patching_pct_revenue": {
    meaning: "Patching (rework) cost as a share of income.",
    formula: ["Typed patching cost divided by total income, times 100.", "Blank when there is no income."],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.patching_pct_cost": {
    meaning: "Patching (rework) cost as a share of total cost.",
    formula: ["Typed patching cost divided by total cost, times 100.", "Blank when total cost is zero."],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
  },
  "calculated.total_labour_hours": {
    meaning: "All hours worked on the project, ordinary and overtime.",
    formula: ["Ordinary hours plus overtime hours.", HOURS_BLANK_LINE],
    source: { kind: "payroll", detail: FROM_PAYROLL },
  },
  "calculated.overtime_hours_pct": {
    meaning: "The share of all hours worked that were overtime.",
    formula: ["Overtime hours divided by total labour hours, times 100.", HOURS_BLANK_LINE],
    source: { kind: "payroll", detail: FROM_PAYROLL },
  },
  "calculated.labour_cost_per_hour": {
    meaning: "What each hour worked cost in wages.",
    formula: ["Wages total divided by total labour hours.", WAGES_TOTAL_LINE, HOURS_SCOPE_LINE, HOURS_BLANK_LINE],
    source: { kind: "mixed", detail: "Wages typed in on the project P&L tab; hours from payroll or typed in. Xero is not counted." },
  },
  "calculated.revenue_per_labour_hour": {
    meaning: "Income earned for each hour worked.",
    formula: ["Total income divided by total labour hours.", HOURS_SCOPE_LINE, HOURS_BLANK_LINE],
    source: { kind: "mixed", detail: "Income from the progress claims; hours from payroll or typed in. Xero is not counted." },
  },
  "calculated.profit_per_labour_hour": {
    meaning: "Profit made for each hour worked.",
    formula: ["Net profit divided by total labour hours.", HOURS_SCOPE_LINE, HOURS_BLANK_LINE],
    source: { kind: "mixed", detail: "Profit from the claims and typed costs; hours from payroll or typed in. Xero is not counted." },
  },
};

const TYPED_ON_PNL_TAB = "On each project's P&L tab (the Status tab). Only typed rows count; Xero is not counted.";

/** Hand-written meanings for the system input keys whose metadata alone would undersell them. */
const INPUT_MEANINGS: Partial<Record<string, string>> = {
  [PNL_METRIC_KEYS.revenue.constructionIncome]: "The value of work claimed in each progress claim, before GST and before retention is held back.",
  [PNL_METRIC_KEYS.revenue.retention]: "The retention held back on each progress claim.",
  [PNL_METRIC_KEYS.budget.materials]: "The materials allowance: a set share of construction income, used instead of actual materials spend.",
  [PNL_METRIC_KEYS.expense.materials]: "Materials spend typed in for each project and month. Shown for reference: the materials budget is taken off profit instead.",
  [PNL_METRIC_KEYS.expense.unmapped]: "Typed costs whose category is not linked to a named metric. They are still taken off profit.",
  [PNL_METRIC_KEYS.labour.overtime]: "Overtime pay for each project and month, from payroll. Shown for reference; it is not taken off profit separately.",
  [PNL_METRIC_KEYS.labour.ordinaryHours]: "Ordinary hours worked on each project each month.",
  [PNL_METRIC_KEYS.labour.overtimeHours]: "Overtime hours worked on each project each month.",
};

/**
 * SEQ-240 audit B, row 26. Production's metric registry marks Construction
 * Income, Retention and Materials Budget as "calculated", but the code adds
 * them the way it adds any input: the database view supplies one value per
 * project and month, and the page sums those months (the RPC reads them off
 * its own totals whatever the registry kind says). Describing them as
 * "calculated" printed "never added across months", which is wrong, and hid
 * the hand-written meanings above. The words follow the code. The registry
 * rows themselves are a data fix, not made here.
 */
const SUMMED_SYSTEM_INPUT_KEYS: ReadonlySet<string> = new Set([
  PNL_METRIC_KEYS.revenue.constructionIncome,
  PNL_METRIC_KEYS.revenue.retention,
  PNL_METRIC_KEYS.budget.materials,
]);

function asSystemInput(metric: DescribableMetric): DescribableMetric {
  if (!SUMMED_SYSTEM_INPUT_KEYS.has(metric.metricKey) || metric.kind !== "calculated") return metric;
  return { ...metric, kind: "input", aggregationMethod: "sum" };
}

const SYSTEM_DEFINITIONS = new Map(DEFAULT_PNL_METRIC_REGISTRY.map((def) => [def.metricKey, def]));

const UNIT_WORDS: Record<PnlUnit, string> = {
  currency: "in dollars",
  currency_per_hour: "in dollars per hour",
  percent: "as a percentage",
  hours: "in hours",
  count: "as a count",
  ratio: "as a ratio",
};

const FOLD_LINES: Record<PnlAggregationMethod, string> = {
  sum: "Adds up the value for every project and month in view.",
  avg: "Averages the project months that have a value. Months with nothing entered are skipped, not counted as zero.",
  min: "Shows the lowest single project month in view.",
  max: "Shows the highest single project month in view.",
  latest: "Adds up the values in the most recent month that has one.",
  calculated: "From other P&L figures, never added across months.",
};

/** The calculated keys whose value depends on payroll (overtime pay or labour hours). */
const PAYROLL_DERIVED_KEYS: ReadonlySet<string> = new Set([
  PNL_METRIC_KEYS.calculated.overtimePctRevenue,
  PNL_METRIC_KEYS.calculated.overtimePctWages,
  PNL_METRIC_KEYS.calculated.totalLabourHours,
  PNL_METRIC_KEYS.calculated.overtimeHoursPct,
  PNL_METRIC_KEYS.calculated.labourCostPerHour,
  PNL_METRIC_KEYS.calculated.revenuePerLabourHour,
  PNL_METRIC_KEYS.calculated.profitPerLabourHour,
]);

/** Whether a metric's figure comes from payroll, directly or through a calculation, so its payroll refresh time is worth showing. */
export function metricUsesPayroll(metricKey: string): boolean {
  return SYSTEM_DEFINITIONS.get(metricKey)?.source === "payroll" || PAYROLL_DERIVED_KEYS.has(metricKey);
}

function isCustomMetric(metric: DescribableMetric): boolean {
  return metric.isCustom === true || /\.custom\./.test(metric.metricKey);
}

function isCalculatedKey(key: string): key is CalculatedMetricKey {
  return Object.prototype.hasOwnProperty.call(CALCULATED_METRIC_FORMULAS, key);
}

function inputSource(metric: DescribableMetric, unit: PnlUnit): MetricDefinition["source"] {
  const key = metric.metricKey;
  if (key === PNL_METRIC_KEYS.revenue.constructionIncome || key === PNL_METRIC_KEYS.revenue.retention) {
    return { kind: "calculated", detail: "From the progress claims (claim reports) for each project. Xero is not counted." };
  }
  if (key === PNL_METRIC_KEYS.budget.materials) {
    return { kind: "calculated", detail: "From construction income and the materials percentage in each project's P&L settings." };
  }
  if (SYSTEM_DEFINITIONS.get(key)?.source === "payroll") {
    return unit === "hours"
      ? { kind: "payroll", detail: FROM_PAYROLL }
      : { kind: "payroll", detail: "Through each project's mapped payroll locations. Blank when no project in view is mapped." };
  }
  if (isCustomMetric(metric) && unit !== "currency") {
    return { kind: "typed", detail: "For each project and month on the project P&L tab, in the operational rows under Wages." };
  }
  return { kind: "typed", detail: TYPED_ON_PNL_TAB };
}

function inputMeaning(metric: DescribableMetric, name: string, unit: PnlUnit): string {
  const written = INPUT_MEANINGS[metric.metricKey];
  if (written) return written;
  if (isCustomMetric(metric) && unit !== "currency") {
    return `${name}: a figure your team types in for each project and month, ${UNIT_WORDS[unit]}. It is never added into a dollar total.`;
  }
  if (unit === "currency") {
    const deducted = metric.deductible === false ? "It is not taken off profit." : "It is taken off profit.";
    return `${name} typed in for each project and month. ${deducted}`;
  }
  return `${name} for each project and month, ${UNIT_WORDS[unit]}.`;
}

function inputFormula(metric: DescribableMetric): string[] {
  const key = metric.metricKey;
  const lines = [FOLD_LINES[resolveAggregationMethod(metric)]];
  if (key === PNL_METRIC_KEYS.revenue.constructionIncome) {
    lines.push("Each claim counts its subtotal. An older claim with no subtotal counts its total ex GST plus its retention.");
  } else if (key === PNL_METRIC_KEYS.budget.materials) {
    lines.push("Construction income times the project's materials percentage (30% when not set).");
  } else if (SYSTEM_DEFINITIONS.get(key)?.multiplierApplies) {
    lines.push("The figure here is as typed. The wages multiplier is applied only inside total cost, net profit and the wages ratios.");
  } else if (SYSTEM_DEFINITIONS.get(key)?.source === "payroll" && resolveMetricUnit(metric) === "hours") {
    lines.push("Hours typed in for a project and month replace the payroll hours for that month; they are not added together.");
  }
  return lines;
}

const DEFAULT_PROJECTS: MetricDefinition["projects"] = { scope: "all-unless-filtered" };
const DEFAULT_DATES: MetricDefinition["dates"] = { scope: "page-range" };
const DEFAULT_PERIOD = "The months in the date range, split the way the chart or table groups them.";

/**
 * The pop-up definition for one registry metric. `context` supplies the
 * projects, dates and period a widget or section adds; without it the text
 * describes the P&L Analytics defaults.
 */
export function describeMetric(rawMetric: DescribableMetric, context: DescribeContext = {}): MetricDefinition {
  const metric = asSystemInput(rawMetric);
  const name = metric.displayName?.trim() || humaniseMetricKey(metric.metricKey);
  const unit = resolveMetricUnit(metric);
  const scope = {
    projects: context.projects ?? DEFAULT_PROJECTS,
    dates: context.dates ?? DEFAULT_DATES,
    period: context.period ?? DEFAULT_PERIOD,
  };

  if (isCalculatedKey(metric.metricKey)) {
    const text = CALCULATED_METRIC_FORMULAS[metric.metricKey];
    return { name, meaning: text.meaning, formula: text.formula, source: text.source, ...scope };
  }

  if (metric.kind === "calculated") {
    // A calculated key this module has no words for: honest and generic.
    return {
      name,
      meaning: `${name}, worked out by the app from other P&L figures, ${UNIT_WORDS[unit]}.`,
      formula: [FOLD_LINES.calculated],
      source: { kind: "calculated", detail: "From the claims, typed P&L rows and payroll. Xero is not counted." },
      ...scope,
    };
  }

  return {
    name,
    meaning: inputMeaning(metric, name, unit),
    formula: inputFormula(metric),
    source: inputSource(metric, unit),
    ...scope,
  };
}
